import { useCallback, useEffect, useRef, useId } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Box,
  Stack,
  Typography,
  Alert,
  AlertTitle,
  IconButton,
  Slide,
} from '@mui/material';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

export interface ModalReportadoTelcoProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  identification?: string;
  reportedBy?: string;
  closeLabel?: string;
  reEnterLabel?: string;
  disableBackdropClose?: boolean;
  disableEscapeClose?: boolean;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

const DEFAULT_TITLE = 'Prospecto reportado TELCOS';
const DEFAULT_MESSAGE =
  'El prospecto que intenta registrar se encuentra reportado por otro operador de telecomunicaciones (TELCOS). Por normativa interna, no es posible continuar con el proceso de venta en este momento.';
const DEFAULT_CLOSE_LABEL = 'Cerrar';
const DEFAULT_MODULE_LABEL = 'EVA · Prospecto';

const PALETTE = {
  primary: '#093C5D',
  primaryDark: '#0A1F5C',
  primaryDarker: '#06163F',
  primaryLight: '#1E3A8A',
  accent: '#3B5BDB',
  onPrimary: '#FFFFFF',
  onPrimaryMuted: '#C7D0E8',
  border: '#C7D0E8',
  borderStrong: '#9DACD2',
  bg: '#F4F6FC',
  bgSoft: '#E8EDF7',
  textPrimary: '#0A1F5C',
  textSecondary: '#3B4A78',
  textMuted: '#5B6A9A',
} as const;

const ModalReportadoTelco = ({
  open,
  onClose,
  title = DEFAULT_TITLE,
  message = DEFAULT_MESSAGE,
  closeLabel = DEFAULT_CLOSE_LABEL,
  disableBackdropClose = false,
  disableEscapeClose = false,
  maxWidth = 'sm',
}: ModalReportadoTelcoProps) => {
  const titleId = useId();
  const descriptionId = useId();
  const previousActiveElement = useRef<HTMLElement | null>(null);

  const handleClose = useCallback(
    (_event: unknown, reason: 'backdropClick' | 'escapeKeyDown') => {
      if (reason === 'backdropClick' && disableBackdropClose) return;
      if (reason === 'escapeKeyDown' && disableEscapeClose) return;
      onClose();
    },
    [onClose, disableBackdropClose, disableEscapeClose],
  );

  useEffect(() => {
    if (!open) return;
    previousActiveElement.current = document.activeElement as HTMLElement | null;
    return () => {
      previousActiveElement.current?.focus?.();
    };
  }, [open]);



  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth
      scroll="paper"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      TransitionComponent={Slide}
      TransitionProps={{ direction: 'up', timeout: 220 } as never}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 1.5,
          overflow: 'hidden',
          //border: `1px solid ${PALETTE.border}`,
          //boxShadow: `0 24px 48px -12px ${PALETTE.primaryDark}55, 0 0 0 1px ${PALETTE.primary}10`,
        },
      }}
      sx={{
        '& .MuiDialog-container': {
          alignItems: { xs: 'flex-end', sm: 'center' },
        },
      }}
    >
      <DialogTitle
        id={titleId}
        sx={{
          p: 0,
          m: 0,
          backgroundColor: PALETTE.primary,
          color: PALETTE.onPrimary,
          borderBottom: `1px solid ${PALETTE.primaryDark}`,
        }}
      >
        <Stack
          direction="row"
          spacing={1.75}
          alignItems="center"
          sx={{ p: '18px 22px' }}
        >
          <Box
            aria-hidden
            sx={{
              flexShrink: 0,
              width: 44,
              height: 44,
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.14)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(255, 255, 255, 0.18)',
            }}
          >
            <ReportProblemRoundedIcon
              sx={{ color: PALETTE.onPrimary, fontSize: 24 }}
            />
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              component="h2"
              sx={{
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                lineHeight: 1.3,
                color: PALETTE.onPrimary,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: PALETTE.onPrimaryMuted,
                letterSpacing: '0.02em',
                display: 'block',
                mt: '2px',
              }}
            >
              {DEFAULT_MODULE_LABEL}
            </Typography>
          </Box>
          <IconButton
            aria-label="Cerrar"
            onClick={onClose}
            size="small"
            sx={{
              color: PALETTE.onPrimaryMuted,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.12)',
                color: PALETTE.onPrimary,
              },
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          marginTop: '1rem',
          padding: '22px',
          backgroundColor: 'background.paper',
        }}
      >
        <Stack spacing={2}>
          <Alert
            severity="warning"
            variant="outlined"
            icon={
              <InfoOutlinedIcon
                fontSize="small"
                sx={{ color: PALETTE.primary }}
              />
            }
            sx={{
              alignItems: 'flex-start',
              backgroundColor: PALETTE.bg,
              borderColor: PALETTE.border,
              borderLeftWidth: 3,
              borderLeftColor: PALETTE.primary,
              borderRadius: 1,
              '& .MuiAlert-message': { width: '100%', py: 0.25 },
            }}
          >
            <AlertTitle
              sx={{
                m: 0,
                fontSize: 13,
                fontWeight: 600,
                letterSpacing: '0.02em',
                textTransform: 'uppercase',
                color: PALETTE.textPrimary,
              }}
            >
              Proceso bloqueado
            </AlertTitle>
            <DialogContentText
              id={descriptionId}
              sx={{
                m: 0,
                fontSize: 14,
                lineHeight: 1.55,
                color: PALETTE.textSecondary,
              }}
            >
              {message}
            </DialogContentText>
          </Alert>

          
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: '22px',
          py: '16px',
          backgroundColor: PALETTE.bg,
          borderTop: `1px solid ${PALETTE.border}`,
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          size="medium"
          sx={{
            minWidth: 120,
            borderColor: PALETTE.borderStrong,
            color: PALETTE.textSecondary,
            fontWeight: 600,
            letterSpacing: '0.02em',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: PALETTE.bgSoft,
              borderColor: PALETTE.primaryLight,
              color: PALETTE.primary,
            },
          }}
        >
          {closeLabel}
        </Button>
        
      </DialogActions>
    </Dialog>
  );
};

export default ModalReportadoTelco;
